import { useMutation, useQueryClient } from "react-query";
import { activateClient, deactivateClient } from "api/customers";
import { useGlobalToast } from "components/GlobalToastProvider";

export interface ActivationPayload {
  id?: string;
  clientId: string;
  reason?: string;
  dropReason?: string;
  churnReason?: string;
  disabledDate?: string;
  note?: string;
  departedTo?: string;
}

export interface UseToggleClientActivationOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
}

/**
 * Custom hook for toggling client activation status (activate or deactivate).
 * Handles both activation (PUT /users/{clientId}/activate-client) and
 * deactivation (PUT /users/{clientId}/deactivate-client) with proper error handling.
 *
 * @example
 * const { toggleActivation, isLoading } = useToggleClientActivation({
 *   onSuccess: (data) => {
 *     showToast({ message: "Success!" });
 *   }
 * });
 *
 * toggleActivation({ clientId: "123" });
 */
export const useToggleClientActivation = (
  options: UseToggleClientActivationOptions = {}
): {
  toggleActivation: (payload: ActivationPayload) => void;
  isLoadingToggleActivation: boolean;
  isToggleActivationError: boolean;
  isToggleActivationSuccess: boolean;
  [key: string]: any;
} => {
  const { onSuccess, onError, onSettled } = options;
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError, isSuccess, ...rest } = useMutation(
    (payload: ActivationPayload) => {
      // Determine if we're activating or deactivating based on payload structure
      // If we have disabledDate, reason, or departedTo, it's a deactivation
      const isDeactivation = payload.disabledDate || payload.reason || payload.departedTo;

      if (isDeactivation) {
        return deactivateClient(payload);
      } else {
        // For activation, we use the activate-client endpoint
        return activateClient(payload);
      }
    },
    {
      onSuccess: (data) => {
        // Invalidate the getHasClientAccountAccess query to refresh access status
        // This ensures the UI reflects the updated client/user access status after activation/deactivation
        queryClient.invalidateQueries(["getHasClientAccountAccess"]);

        onSuccess?.(data);
      },
      onError: (error: unknown) => {
        console.error("Error toggling client activation:", error);
        showToast({
          message: "Whoops! Client account activation/deactivation failed",
          errorState: true,
        });
        onError?.(error);
      },
      onSettled: () => {
        onSettled?.();
      },
    }
  );

  return {
    toggleActivation: mutate,
    isLoadingToggleActivation: isLoading,
    isToggleActivationError: isError,
    isToggleActivationSuccess: isSuccess,
    ...rest,
  };
};

export default useToggleClientActivation;
